import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption, Transition, CloseButton } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, PlusIcon, PencilSquareIcon, XMarkIcon, TrashIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { getTagsByCompanyId, addEditTag, deleteTag } from 'services/leadTagsService';
import { toast } from 'sonner';
import { ConfirmModal } from 'components/shared/ConfirmModal';

const PREDEFINED_COLORS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Green', value: '#10b981' },
  { name: 'Yellow', value: '#f59e0b' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Slate', value: '#64748b' },
];

/**
 * LeadTagSelector
 * 
 * A component to select, add, and edit lead tags with checkbox multi-select.
 * 
 * @param {Array} value - Array of selected tag IDs.
 * @param {Function} onChange - Callback when selected tags change.
 */
export const LeadTagSelector = ({ value = [], onChange, className, defaultOpen = false, inline = false, onClose }) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  // Local state for pending selection
  const [selectedIds, setSelectedIds] = useState(Array.isArray(value) ? value : []);
  const comboboxButtonRef = useRef(null);

  // Sync local state when external value changes (e.g. on mount or drawer refresh)
  useEffect(() => {
    setSelectedIds(Array.isArray(value) ? value : []);
  }, [value]);

  // Programmatically open the dropdown if defaultOpen is true
  useEffect(() => {
    if (defaultOpen && !inline) {
      const timer = setTimeout(() => {
        if (comboboxButtonRef.current) {
          comboboxButtonRef.current.click();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [defaultOpen, inline]);

  // State for Add/Edit Form
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState(PREDEFINED_COLORS[0].value);
  const [isSaving, setIsSaving] = useState(false);

  // State for ConfirmModal
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalState, setConfirmModalState] = useState('pending');
  const [tagToDelete, setTagToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const response = await getTagsByCompanyId(1, 10000, '', 1); // 1 = General (Lead tags)
      if (response.success) {
        setAvailableTags(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedTags = useMemo(() => {
    return (Array.isArray(value) ? value : [])
      .map(id => availableTags.find(tag => String(tag.Id) === String(id)))
      .filter(Boolean);
  }, [value, availableTags]);

  const filteredTags = query === ''
    ? availableTags
    : availableTags.filter((tag) =>
      tag.TagName.toLowerCase().includes(query.toLowerCase())
    );

  const handleSelect = (tag) => {
    const tagId = tag.Id;
    const currentValue = Array.isArray(selectedIds) ? selectedIds : (Array.isArray(value) ? value : []);
    const isSelected = currentValue.some(id => String(id) === String(tagId));
    let nextValue;
    if (isSelected) {
      nextValue = currentValue.filter(id => String(id) !== String(tagId));
    } else {
      nextValue = [...currentValue, tagId];
    }
    setSelectedIds(nextValue);
    onChange(nextValue);
  };

  const handleApply = () => {
    const addedIds = selectedIds.filter(id => !value.includes(id));
    const removedIds = value.filter(id => !selectedIds.includes(id));

    if (addedIds.length > 0 && removedIds.length === 0) {
      toast.success(`${addedIds.length} tag(s) applied`);
    } else if (removedIds.length > 0 && addedIds.length === 0) {
      toast.info(`${removedIds.length} tag(s) removed`);
    } else if (addedIds.length > 0 && removedIds.length > 0) {
      toast.success(`Tags updated successfully`);
    }

    onChange(selectedIds);

    // Close the combobox dropdown by programmatically clicking the combobox button
    if (!inline) {
      setTimeout(() => {
        if (comboboxButtonRef.current) {
          comboboxButtonRef.current.click();
        }
      }, 50);
    }
  };

  const handleToggleTag = (tag) => {
    const isSelected = value.some(id => String(id) === String(tag.Id));
    let newValue;
    if (isSelected) {
      newValue = value.filter(id => String(id) !== String(tag.Id));
      toast.success(`Tag "${tag.TagName}" removed`);
    } else {
      newValue = [...value, tag.Id];
      toast.success(`Tag "${tag.TagName}" added`);
    }
    onChange(newValue);
  };

  const openAddForm = (e) => {
    e?.stopPropagation();
    setEditingTag(null);
    setTagName(query || '');
    setTagColor(PREDEFINED_COLORS[0].value);
    setIsFormOpen(true);
  };

  const openEditForm = (e, tag) => {
    e?.stopPropagation();
    setEditingTag(tag);
    setTagName(tag.TagName);
    setTagColor(tag.ColorCode || PREDEFINED_COLORS[0].value);
    setIsFormOpen(true);
  };

  const handleSaveTag = async () => {
    if (!tagName.trim()) {
      toast.error('Tag name is required');
      return;
    }

    setIsSaving(true);
    try {
      const tagData = {
        TagName: tagName.trim(),
        ColorCode: tagColor,
        Id: editingTag?.Id || undefined,
      };

      const response = await addEditTag(tagData);
      if (response.success) {
        toast.success(`Tag ${editingTag ? 'updated' : 'added'} successfully`);
        setIsFormOpen(false);
        await fetchTags();

        if (!editingTag && response.data) {
          const newTag = typeof response.data === 'string' ? { Id: response.data } : response.data;
          if (newTag?.Id) {
            setSelectedIds(prev => [...prev, newTag.Id]);
            const currentValue = Array.isArray(value) ? value : [];
            if (!currentValue.some(id => String(id) === String(newTag.Id))) {
              onChange([...currentValue, newTag.Id]);
            }
          }
        }
      } else {
        toast.error(response.error || 'Failed to save tag');
      }
    } catch (error) {
      console.error('Error saving tag:', error);
      toast.error('An error occurred while saving the tag');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTag = (e, tag) => {
    e?.stopPropagation();
    setTagToDelete(tag);
    setErrorMessage('');
    setConfirmModalState('pending');
    setIsConfirmModalOpen(true);
  };

  const onConfirmDelete = async () => {
    if (!tagToDelete) return;

    setIsDeleting(true);
    try {
      const response = await deleteTag(tagToDelete.Id);
      if (response.success) {
        setConfirmModalState('success');
        toast.success("Tag removed successfully");
        await fetchTags();

        if (selectedIds.includes(tagToDelete.Id)) {
          setSelectedIds(selectedIds.filter(id => id !== tagToDelete.Id));
        }

        if (value.includes(tagToDelete.Id)) {
          onChange(value.filter(id => id !== tagToDelete.Id));
        }

        if (editingTag?.Id === tagToDelete.Id) {
          setIsFormOpen(false);
          setEditingTag(null);
        }

        setTimeout(() => {
          setIsConfirmModalOpen(false);
          setTagToDelete(null);
        }, 1500);
      } else {
        setConfirmModalState('error');
        const errMessage = response.error || 'Failed to delete tag';
        setErrorMessage(errMessage);
        toast.error(errMessage);
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      setConfirmModalState('error');
      const errMessage = 'An error occurred while deleting the tag';
      setErrorMessage(errMessage);
      toast.error(errMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={clsx("flex flex-col gap-1.5", className)}>
      {/* Selected Tags Chips (Persistent) */}
      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
        Tags
      </span>
      <div className="flex flex-wrap gap-1 min-h-0">
        {selectedTags.length > 0 ? (
          selectedTags.map(tag => (
            <span
              key={tag.Id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold text-white shadow-sm transition-all hover:scale-105"
              style={{ backgroundColor: tag.ColorCode || '#6b7280' }}
            >
              {tag.TagName}
              <button
                type="button"
                onClick={() => handleToggleTag(tag)}
                className="hover:bg-black/10 rounded-full p-0.5 transition-colors"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </span>
          ))
        ) : (
          <span className="text-[10px] text-gray-400 italic py-1">No tags selected</span>
        )}
      </div>

      {/* Tag Selector Combobox / Inline List */}
      {!isFormOpen && (
        inline ? (
          <div className="mt-1 flex flex-col gap-2">
            {/* Search Input Box */}
            <div className="relative w-full rounded-lg bg-white dark:bg-dark-700 text-left border border-gray-300 dark:border-dark-600 focus-within:ring-1 focus-within:ring-primary-500 transition-all duration-200">
              <input
                autoFocus
                type="text"
                className="w-full border-none py-1.5 pl-3 pr-10 text-xs leading-5 text-gray-900 dark:text-gray-100 focus:ring-0 bg-transparent focus:outline-none"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search or add tags..."
              />
            </div>

            {/* List and Create Row */}
            <div className="rounded-2xl bg-white dark:bg-dark-700 py-1.5 text-xs border border-gray-100 dark:border-dark-600 flex flex-col overflow-hidden">
              {/* Add New Option & Close Button */}
              <div className="relative select-none py-2 px-3 flex items-center justify-between border-b border-gray-50 dark:border-dark-600/50">
                <div
                  className="cursor-pointer text-primary-600 dark:text-primary-400 font-bold flex items-center gap-2 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors py-1 px-2 rounded-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openAddForm(e);
                  }}
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>Create &quot;{query || "New Tag"}&quot;</span>
                </div>
                {onClose && (
                  <button
                    type="button"
                    className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onClose();
                    }}
                    title="Close"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Scrollable list of tags */}
              <div className="max-h-40 overflow-auto">
                {filteredTags.length === 0 && query !== '' ? (
                  <div className="relative cursor-default select-none py-4 px-4 text-gray-500 text-center italic">
                    No matching tags found.
                  </div>
                ) : (
                  filteredTags.map((tag) => {
                    const isSelected = selectedIds.some(id => String(id) === String(tag.Id));
                    return (
                      <div
                        key={tag.Id}
                        className={clsx(
                          'group relative cursor-pointer select-none py-2.5 pl-10 pr-12 transition-all hover:bg-gray-50 dark:hover:bg-dark-800/50',
                          'text-gray-900 dark:text-gray-200'
                        )}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSelect(tag);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {/* Checkbox UI */}
                          <div className={clsx(
                            "w-4 h-4 rounded border flex items-center justify-center transition-all shrink-0",
                            isSelected
                              ? "bg-primary-600 border-primary-600"
                              : "border-gray-300 dark:border-dark-500"
                          )}>
                            {isSelected && (
                              <CheckIcon className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: tag.ColorCode || '#6b7280' }}
                          />
                          <span className={clsx('block truncate pr-2', isSelected ? 'font-bold' : 'font-normal')}>
                            {tag.TagName}
                          </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="absolute inset-y-0 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openEditForm(e, tag);
                            }}
                            className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                            title="Edit Tag"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteTag(e, tag);
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Delete Tag"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="relative">
            <Combobox
              value={selectedIds}
              onChange={() => { }}
              multiple
              immediate
            >
              {({ open }) => (
                <div className="relative mt-1">
                  <div className="relative w-full cursor-default h-full rounded-lg bg-white dark:bg-dark-700 text-left border border-gray-300 dark:border-dark-600 focus-within:ring-1 focus-within:ring-primary-500 transition-all duration-200">
                    <ComboboxInput
                      className="w-full border-none py-1.5 pl-3 pr-10 text-xs leading-5 text-gray-900 dark:text-gray-100 focus:ring-0 bg-transparent cursor-pointer"
                      onChange={(event) => setQuery(event.target.value)}
                      onClick={() => {
                        if (!open && comboboxButtonRef.current) {
                          comboboxButtonRef.current.click();
                        }
                      }}
                      placeholder="Search or add tags..."
                    />
                    <ComboboxButton ref={comboboxButtonRef} className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon
                        className="h-4 w-4 text-gray-400"
                        aria-hidden="true"
                      />
                    </ComboboxButton>
                  </div>
                  <Transition
                    as={React.Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}
                  >
                    <ComboboxOptions
                      anchor={{ to: 'bottom start', gap: 8 }}
                      className="w-[var(--input-width)] rounded-2xl bg-white dark:bg-dark-700 py-1.5 text-xs shadow-xl border border-gray-100 dark:border-dark-600 focus:outline-none z-50"
                    >
                      {/* Add New Option & Close Button */}
                      <div className="relative select-none py-2 px-3 flex items-center justify-between border-b border-gray-50 dark:border-dark-600/50">
                        <div
                          className="cursor-pointer text-primary-600 dark:text-primary-400 font-bold flex items-center gap-2 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors py-1 px-2 rounded-lg"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openAddForm(e);
                          }}
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>Create &quot;{query || "New Tag"}&quot;</span>
                        </div>
                        <button
                          type="button"
                          className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (comboboxButtonRef.current) {
                              comboboxButtonRef.current.click();
                            }
                          }}
                          title="Close"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="max-h-64 overflow-auto">
                        {filteredTags.length === 0 && query !== '' ? (
                          <div className="relative cursor-default select-none py-4 px-4 text-gray-500 text-center italic">
                            No matching tags found.
                          </div>
                        ) : (
                          filteredTags.map((tag) => {
                            const isSelected = selectedIds.some(id => String(id) === String(tag.Id));
                            return (
                              <ComboboxOption
                                key={tag.Id}
                                className={({ focus }) =>
                                  clsx(
                                    'relative cursor-pointer select-none py-2.5 pl-10 pr-12 transition-all',
                                    focus ? 'bg-gray-50 dark:bg-dark-800/50' : 'text-gray-900 dark:text-gray-200'
                                  )
                                }
                                value={tag}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSelect(tag);
                                }}
                              >
                                {({ focus }) => (
                                  <>
                                    <div className="flex items-center gap-2" onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleSelect(tag);
                                    }}>
                                      {/* Checkbox UI */}
                                      <div className={clsx(
                                        "w-4 h-4 rounded border flex items-center justify-center transition-all shrink-0",
                                        isSelected
                                          ? "bg-primary-600 border-primary-600"
                                          : "border-gray-300 dark:border-dark-500"
                                      )}>
                                        {isSelected && (
                                          <CheckIcon className="w-3 h-3 text-white" />
                                        )}
                                      </div>
                                      <div
                                        className="w-3 h-3 rounded-full shrink-0"
                                        style={{ backgroundColor: tag.ColorCode || '#6b7280' }}
                                      />
                                      <span className={clsx('block truncate', isSelected ? 'font-bold' : 'font-normal')}>
                                        {tag.TagName}
                                      </span>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className={clsx(
                                      "absolute inset-y-0 right-2 flex items-center gap-1 transition-opacity",
                                      focus ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                                    )}>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          openEditForm(e, tag);
                                        }}
                                        className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                                        title="Edit Tag"
                                      >
                                        <PencilSquareIcon className="w-4 h-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleDeleteTag(e, tag);
                                        }}
                                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                        title="Delete Tag"
                                      >
                                        <TrashIcon className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </>
                                )}
                              </ComboboxOption>
                            );
                          })
                        )}
                      </div>
                    </ComboboxOptions>
                  </Transition>
                </div>
              )}
            </Combobox>
          </div>
        )
      )}

      {/* Add/Edit Form */}
      {isFormOpen && (
        <div className="rounded-xl border border-primary-100 dark:border-primary-900/30 bg-primary-50/30 dark:bg-primary-900/10 p-3 space-y-3 animate-in fade-in zoom-in duration-200">
          <div className="flex items-center justify-between">
            <h5 className="text-xs font-semibold text-primary-700 dark:text-primary-300">
              {editingTag ? 'Edit Tag' : 'New Tag'}
            </h5>
            <button
              type="button"
              onClick={() => setIsFormOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            <input
              type="text"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              placeholder="Tag Name"
              className="w-full rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-700 px-2 py-1.5 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:text-gray-100"
              autoFocus
            />

            <div className="flex flex-wrap gap-1.5">
              {PREDEFINED_COLORS.map(color => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setTagColor(color.value)}
                  className={clsx(
                    "w-5 h-5 rounded-full border-2 transition-all",
                    tagColor === color.value ? "border-primary-600 scale-110 shadow-md ring-2 ring-primary-100 dark:ring-primary-900/30" : "border-transparent hover:scale-105"
                  )}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
              <label className="relative cursor-pointer group">
                <input
                  type="color"
                  value={tagColor}
                  onChange={(e) => setTagColor(e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />

                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="size-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m15 11.25 1.5 1.5.75-.75V8.758l2.276-.61a3 3 0 1 0-3.675-3.675l-.61 2.277H12l-.75.75 1.5 1.5M15 11.25l-8.47 8.47c-.34.34-.8.53-1.28.53s-.94.19-1.28.53l-.97.97-.75-.75.97-.97c.34-.34.53-.8.53-1.28s.19-.94.53-1.28L12.75 9M15 11.25 12.75 9" />
                </svg>
              </label>
            </div>
          </div>

          <div className="flex justify-between items-center pt-1">
            {editingTag ? (
              <button
                type="button"
                onClick={(e) => handleDeleteTag(e, editingTag)}
                className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                <TrashIcon className="w-3 h-3" />
                Delete
              </button>
            ) : <div />}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsFormOpen(false)}
                className="px-2.5 py-1 text-[10px] font-semibold text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTag}
                disabled={isSaving}
                className="px-3 py-1 text-[10px] font-bold text-white bg-primary-600 hover:bg-primary-700 rounded-md shadow-sm transition-all disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : editingTag ? 'Update Tag' : 'Add Tag'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onOk={onConfirmDelete}
        confirmLoading={isDeleting}
        state={confirmModalState}
        buttonColor="error"
        messages={{
          pending: {
            title: 'Delete Tag',
            description: `Are you sure you want to delete the tag "${tagToDelete?.TagName}"? This action cannot be undone.`,
            actionText: 'Delete Tag'
          },
          error: {
            title: 'Opps... Something failed.',
            description: errorMessage || "Ensure internet is on and retry. Contact support if issue remains.",
            actionText: 'Retry'
          }
        }}
      />
    </div>
  );
};
